import React, {useEffect, useState, useSyncExternalStore} from 'react';
import {alpha, Box, Button, Container, Paper, Stack, Typography} from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import {useNavigate} from 'react-router-dom';
import ApiService from '../network/API';
import {confirmDialog} from '../confirm';

// Состояние блокировки материалов на время экзамена: одно на весь сайт, обновляется раз в 15 секунд и сразу, когда сервер ответил «идёт экзамен».
let state = {active: false, until: null, since: null, participant: false, loaded: false, skew: 0};
const listeners = new Set();
let timer = null;

const refresh = async () => {
    try {
        const s = await ApiService.getExamLock();
        // skew — разница серверных и локальных часов: таймер идёт по серверному времени.
        state = {...s, loaded: true, skew: s.serverNow ? new Date(s.serverNow).getTime() - Date.now() : 0};
        listeners.forEach((l) => l());
    } catch (e) {
        // сервер недоступен — оставляем прежнее состояние
    }
};

const subscribe = (listener) => {
    listeners.add(listener);
    if (!timer) {
        refresh();
        timer = setInterval(refresh, 15000);
        window.addEventListener('exam-lock-changed', refresh);
    }
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && timer) {
            clearInterval(timer);
            timer = null;
            window.removeEventListener('exam-lock-changed', refresh);
        }
    };
};

export const useExamLock = () => useSyncExternalStore(subscribe, () => state);

const untilText = (iso) => (iso ? new Date(iso).toLocaleTimeString('ru-RU', {timeZone: 'Europe/Samara', hour: '2-digit', minute: '2-digit'}) : null);

// Оставшееся время в миллисекундах до until по серверным часам; обновляется каждую секунду.
const useRemaining = (until, skew) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    return until ? new Date(until).getTime() - (now + skew) : null;
};

const pad = (n) => String(n).padStart(2, '0');
const splitTime = (ms) => {
    const total = Math.max(0, Math.ceil(ms / 1000));
    return {h: Math.floor(total / 3600), m: Math.floor((total % 3600) / 60), s: total % 60};
};

// Кольцо прогресса: тонкая дорожка и дуга с градиентом, которая убывает по часовой стрелке до конца экзамена. percent == null — длительность неизвестна, дуга полная.
const Ring = ({percent, size = 280, children}) => {
    const stroke = 10;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const value = percent === null ? 100 : percent;
    return (
        <Box sx={{position: 'relative', width: {xs: 236, sm: size}, height: {xs: 236, sm: size}, mx: 'auto'}}>
            <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`} style={{transform: 'rotate(-90deg)'}}>
                <defs>
                    <linearGradient id="examRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#ffb74d"/>
                        <stop offset="100%" stopColor="#ff7043"/>
                    </linearGradient>
                </defs>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth={stroke}/>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="url(#examRingGradient)" strokeWidth={stroke} strokeLinecap="round"
                        strokeDasharray={c} strokeDashoffset={c * (1 - value / 100)}
                        style={{transition: 'stroke-dashoffset 1s linear', filter: 'drop-shadow(0 0 6px rgba(255,152,0,0.45))'}}/>
            </svg>
            <Box sx={{position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>{children}</Box>
        </Box>
    );
};

// Время одной ровной строкой: двоеточия приглушены и стоят по центру цифр.
const Clock = ({t}) => {
    const parts = t.h > 0 ? [String(t.h), pad(t.m), pad(t.s)] : [pad(t.m), pad(t.s)];
    return (
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums', lineHeight: 1}}>
            {parts.map((part, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <Box component="span" sx={{mx: 0.5, fontSize: {xs: 34, sm: 42}, fontWeight: 300, color: 'text.disabled', transform: 'translateY(-2px)'}}>:</Box>}
                    <Box component="span" sx={{fontSize: {xs: 42, sm: 56}, fontWeight: 800, letterSpacing: '-0.03em'}}>{part}</Box>
                </React.Fragment>
            ))}
        </Box>
    );
};

// Страница вместо материалов, пока идёт экзамен: обратный отсчёт до конца и кольцо, которое убывает вместе со временем.
export const ExamLockScreen = ({until, since, skew = 0, participant = false}) => {
    const navigate = useNavigate();
    const left = useRemaining(until, skew);
    // Когда время вышло, просим сайт перечитать состояние: блокировка снимется, как только закончится последняя попытка.
    useEffect(() => {
        if (left !== null && left <= 0) {
            window.dispatchEvent(new Event('exam-lock-changed'));
            const t = setInterval(() => window.dispatchEvent(new Event('exam-lock-changed')), 5000);
            return () => clearInterval(t);
        }
        return undefined;
    }, [left !== null && left <= 0]); // eslint-disable-line react-hooks/exhaustive-deps

    const total = until && since ? new Date(until).getTime() - new Date(since).getTime() : null;
    const percent = left !== null && total && total > 0 ? Math.min(100, Math.max(0, (left / total) * 100)) : null;
    const t = left === null ? null : splitTime(left);
    const finishing = left !== null && left <= 0;

    return (
        <Container maxWidth="sm" sx={{py: {xs: 4, sm: 8}}}>
            <Paper variant="outlined" sx={{
                px: {xs: 2.5, sm: 6}, py: {xs: 4, sm: 6}, textAlign: 'center', borderRadius: 5,
                background: (theme) => `radial-gradient(ellipse at 50% -10%, ${theme.palette.warning.main}26, transparent 55%)`,
            }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{mb: 0.5}}>
                    <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', animation: 'examPulse 1.6s ease-in-out infinite',
                        '@keyframes examPulse': {'0%,100%': {opacity: 1, transform: 'scale(1)'}, '50%': {opacity: 0.35, transform: 'scale(0.7)'}}}}/>
                    <Typography variant="overline" sx={{letterSpacing: '0.2em', color: 'warning.main', fontWeight: 700, lineHeight: 1.5}}>Экзамен идёт</Typography>
                </Stack>
                <Typography variant="h5" fontWeight={800} sx={{mb: 4}}>Материалы временно закрыты</Typography>

                <Ring percent={percent}>
                    {t ? (
                        finishing ? (
                            <Typography variant="h5" fontWeight={700} color="text.secondary">Завершается…</Typography>
                        ) : (
                            <>
                                <Clock t={t}/>
                                <Typography variant="caption" color="text.secondary" sx={{mt: 1.5, textTransform: 'uppercase', letterSpacing: '0.18em'}}>осталось</Typography>
                            </>
                        )
                    ) : (
                        <Typography color="text.secondary">…</Typography>
                    )}
                </Ring>

                {until && (
                    <Box sx={{display: 'inline-block', mt: 4, px: 2, py: 0.75, borderRadius: 999, bgcolor: 'action.hover'}}>
                        <Typography variant="body2">Откроется примерно в <b>{untilText(until)}</b> (Самара)</Typography>
                    </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{mt: 2}}>
                    Лекции и задачи недоступны всем, пока идёт экзамен. Страница обновится сама.
                </Typography>
                {participant && (
                    <Button variant="contained" sx={{mt: 3, borderRadius: 3, px: 4}} onClick={() => navigate('/exams')}>
                        К моему экзамену
                    </Button>
                )}
            </Paper>
        </Container>
    );
};

// Оборачивает страницы с материалами: во время экзамена показывает заглушку всем, кроме админа и студентов, у которых идёт экзамен.
// blockParticipants: закрыть и для тех, у кого идёт экзамен (лекции, разделы). Страница задачи для них открыта: там задачи их билета.
export const ExamLockGate = ({children, blockParticipants = false}) => {
    const lock = useExamLock();
    if (lock.active && !lock.exempt && !ApiService.isAdmin() && (blockParticipants || !lock.participant)) {
        return <ExamLockScreen until={lock.until} since={lock.since} skew={lock.skew} participant={lock.participant}/>;
    }
    return children;
};

// Плашка сверху: идёт экзамен; админу — кнопка аварийного снятия блокировки.
export const ExamLockBanner = () => {
    const lock = useExamLock();
    const [busy, setBusy] = useState(false);
    const left = useRemaining(lock.active ? lock.until : null, lock.skew);
    if (!lock.active) return null;
    const t = left === null || left <= 0 ? null : splitTime(left);
    const admin = ApiService.isAdmin();
    const release = async () => {
        if (!await confirmDialog('Снять блокировку материалов? Лекции и задачи сразу откроются для всех, даже если экзамен ещё идёт. При новой выдаче доступа блокировка включится снова.')) return;
        setBusy(true);
        try {
            await ApiService.releaseExamLock();
            window.dispatchEvent(new Event('exam-lock-changed'));
        } finally {
            setBusy(false);
        }
    };
    const timer = t ? `${t.h > 0 ? `${t.h}:` : ''}${pad(t.m)}:${pad(t.s)}` : null;
    return (
        <Box sx={{maxWidth: 'lg', mx: 'auto', width: '100%', px: 2, pt: 2}}>
            <Paper variant="outlined" sx={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, px: 2, py: 1.25, borderRadius: 2.5,
                borderColor: 'warning.main',
                background: (theme) => `linear-gradient(90deg, ${alpha(theme.palette.warning.main, 0.16)}, ${alpha(theme.palette.warning.main, 0.04)})`,
            }}>
                <Box sx={{
                    width: 36, height: 36, borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0,
                    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.2), color: 'warning.main',
                }}>
                    <AssignmentOutlinedIcon fontSize="small"/>
                </Box>
                <Box sx={{flex: 1, minWidth: 180}}>
                    <Typography fontWeight={700} lineHeight={1.25}>Идёт экзамен</Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.3}>
                        {lock.until ? `Лекции и задачи закрыты до ${untilText(lock.until)} (Самара)` : 'Лекции и задачи закрыты'}
                        {admin ? ' · вы видите материалы как админ' : ''}
                    </Typography>
                </Box>
                {timer && (
                    <Typography component="span" fontWeight={800} sx={{
                        fontVariantNumeric: 'tabular-nums', fontSize: '1.25rem', letterSpacing: '0.02em', color: 'warning.main',
                    }}>
                        {timer}
                    </Typography>
                )}
                {admin && <Button color="warning" size="small" variant="outlined" disabled={busy} onClick={release}>Снять блокировку</Button>}
            </Paper>
        </Box>
    );
};
